import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { TruckIcon, SignalIcon, SignalSlashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface VehicleOnlineSummary {
  totalCount: number;
  onlineCount: number;
  offlineCount: number;
}

const VehicleListWidget: React.FC = () => {
  const { t } = useTranslation();
  const [summary, setSummary] = useState<VehicleOnlineSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/dashboard/data/vehicle-online-summary');
      if (response.data.success) {
        setSummary(response.data.data);
        setError(null);
      } else {
        setError(response.data.error || 'Failed to load vehicles');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load vehicles';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSummary();
  }, []);

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !summary) {
    return <div className="text-red-500 text-sm py-2">{error}</div>;
  }

  const totalCount = summary?.totalCount ?? 0;
  const onlineCount = summary?.onlineCount ?? 0;
  const offlineCount = summary?.offlineCount ?? 0;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <TruckIcon className="w-8 h-8 text-blue-500" />
          <div>
            <div className="text-2xl font-bold text-textDefault">{totalCount}</div>
            <div className="text-xs text-textSecondary">
              {t('vehicleListWidget.totalVehicles')}
            </div>
          </div>
        </div>

        <button
          onClick={() => void fetchSummary()}
          className="p-1 hover:bg-bgElevated rounded"
          title={t('common.refresh')}
        >
          <ArrowPathIcon className={`w-4 h-4 text-textSecondary ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-borderDefault">
        <div className="flex items-center gap-1.5 text-green-500">
          <SignalIcon className="w-4 h-4" />
          <span className="font-semibold">{onlineCount}</span>
          <span className="text-xs text-textTertiary">online</span>
        </div>
        <div className="flex items-center gap-1.5 text-textTertiary">
          <SignalSlashIcon className="w-4 h-4" />
          <span className="font-semibold">{offlineCount}</span>
          <span className="text-xs text-textTertiary">offline</span>
        </div>
      </div>
    </div>
  );
};

export default VehicleListWidget;
